/**
 * Single page for hotspots
 */

// Import statements
import React from "react"
import {graphql, Link} from "gatsby"
import {GatsbyImage} from "gatsby-plugin-image"
import parse from "html-react-parser"
import "../styles/files/single-post.scss"

// We're using Gutenberg so we need the block styles
// these are copied into this project due to a conflict in the postCSS
// version used by the Gatsby and @wordpress packages that causes build
// failures.
// @todo update this once @wordpress upgrades their postcss version
//import "../styles/@wordpress/block-library/build-style/style.styles"
//import "../styles/@wordpress/block-library/build-style/theme.styles"
import Layout from "../components/layout"
import Body from "../components/body"
import Seo from "../components/common/seo"
import {formatDate} from "../components/common/format-date";
import AdFooter from "../components/ad/ad-footer";
import Author from "../components/common/author";
import AdSkyscraper from "../components/ad/ad-skyscraper";
import InstagramFeed from "../components/common/instagram-feed";
import SocialMedia from "../components/common/socialmedia";
import BlogComments from "../components/comment/comments";
import {HotspotRating} from "../components/common/rating";

/**
 * Define the hotspotPostTemplate constant
 * @param previous
 * @param next
 * @param hotspot
 * @param comments
 * @returns {JSX.Element}
 */
const HotspotPostTemplate = ({data: {previous, next, hotspot, comments}}) => {

    // define the featuredImage of this post
    const featuredImage = {
        data: hotspot.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData,
        alt: hotspot.featuredImage?.node?.alt || ``,
    }

    // get the publication date
    const publication_date = new Date(hotspot.date);

    // return the information
    return (
        <Layout>
            <Body bodyClass="single single-hotspot"/>
            <Seo title={hotspot.title} description={hotspot.excerpt}/>

            <article className="post">

                <header className="row">
                    <div className="col-md-12 col-lg-10 offset-lg-1">
                        <h1 className="entry-title" itemProp="name">{parse(hotspot.title)}</h1>
                        {(hotspot.nameText !== "" || hotspot.addressText !== "" || hotspot.cityText !== "") && (
                            <ul className="list-inline uppercase">
                                {hotspot.nameText && (
                                    <li key="name">{hotspot.nameText}</li>
                                )}
                                {hotspot.addressText && (
                                    <li key="streetAddress">{hotspot.addressText}</li>
                                )}
                                {hotspot.cityText && (
                                    <li key="addressLocality">{hotspot.cityText}</li>
                                )}
                            </ul>
                        )}
                        <ul className="list-inline uppercase">
                            <li key="website">
                                <a href={hotspot.urlLinkText}
                                   title={hotspot.urlAnchorText || hotspot.urlLinkText}>{hotspot.urlAnchorText || hotspot.urlLinkText}</a>
                            </li>
                        </ul>
                        <ul className="list-inline uppercase">
                            <li key="publication_date">{formatDate(publication_date)}</li>
                            <li key="author">Door {hotspot.author.node.uri && (
                                <Link to={hotspot.author.node.uri}>{hotspot.author.node.name}</Link>
                            )}</li>
                        </ul>
                    </div>
                </header>

                <div className="row" id="header-single-image">
                    <nav className="d-flex justify-content-between">
                        <ul className="pager">
                            {previous && (
                                <li className="pager-button btn btn-grey previous">
                                    <Link to={previous.uri} rel="prev" className="tip tip-left"
                                          data-title={parse(previous.title)}>
                                        ← vorige
                                    </Link>
                                </li>
                            )}
                            {next && (
                                <li className="pager-button btn btn-grey next">
                                    <Link to={next.uri} rel="next" className="tip tip-right"
                                          data-title={parse(next.title)}>
                                        volgende →
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </nav>
                    <div className="col-md-10 offset-md-1">
                        {/* if we have a featured image for this post let's display it */}
                        {featuredImage?.data && (
                            <GatsbyImage
                                image={featuredImage.data}
                                alt={featuredImage.alt}
                            />
                        )}
                    </div>
                </div>

                <div className="row single-content" id="post-content">
                    <div className="col-md-8 col-lg-7 offset-lg-1 order-1 col-no-padding" id="post-content-body">

                        <AdFooter/>

                        <div id="single-blog-text">
                            <div id="result"></div>
                            {!!hotspot.content && parse(hotspot.content)}
                        </div>
                        <div id="single-footer">
                            <HotspotRating rating={hotspot.rating}/>
                            <div id="author-single-meta">
                                <Author author={hotspot.author.node}/>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 col-lg-3 order-2" id="sidebar-right">
                        <AdSkyscraper/>
                        <InstagramFeed/>
                        <SocialMedia title={hotspot.title} featuredImage={featuredImage}/>
                    </div>
                </div>

                <BlogComments post={hotspot} comments={comments}/>

            </article>

        </Layout>
    )
}

/**
 * Export the hotspotPostTemplate variable
 */
export default HotspotPostTemplate


/**
 * Get the single hotspot
 * @type {StaticQueryDocument}
 */
export const pageQuery = graphql`
  query HotspotPostById(
    $id: String!
    $previousPostId: String
    $nextPostId: String
  ) {
    hotspot: wpHotspot(id: { eq: $id }) {
        id
        content
        title
        nameText
        addressText
        postalText
        cityText
        countryText
        phonenumberText
        urlLinkText
        urlAnchorText
        priceText
        rating
        ratingDescription
        date(formatString: "MMMM DD, YYYY")
        featuredImage {
          node {
            altText
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 50
                  placeholder: DOMINANT_COLOR
                  layout: FULL_WIDTH
                  breakpoints: [546,605,805,955,1105]
                )
              }
            }
          }
        }
        author {
          node {
            databaseId
            name
            uri
            description
            nicename
            email
            slug
            avatar {
              height
              size
              url
              width
            }
          }
        }
      }
      comments: allWpComment(filter: {commentedOn: {node: {id: { eq: $id } } } } ) {
        edges {
          node {
            id
            databaseId
            author {
              node {
                id
                name
                email
                url
                databaseId
              }
            }
            date(formatString: "YYYY-MM-DDTHH:mm:ss.sssZ")
            content
            parentDatabaseId
          }
        }
      }
      previous: wpHotspot(id: { eq: $previousPostId }) {
        uri
        title
      }
      next: wpHotspot(id: { eq: $nextPostId }) {
        uri
        title
      }
  }
`
